import { useEffect, useState, useRef, useCallback } from "react";
import store from "store";
import { useAppDispatch, useAppSelector } from "../Hooks/redux";
import { meRequest, selectUser } from "../Redux/AuthRedux";

const LANGUAGE_NAMES = {
  es: "Spanish", fr: "French", de: "German", pt: "Portuguese",
  it: "Italian", ja: "Japanese", ko: "Korean", zh: "Chinese",
  hi: "Hindi", ar: "Arabic", ru: "Russian", nl: "Dutch",
  pl: "Polish", sv: "Swedish", tr: "Turkish", th: "Thai",
  vi: "Vietnamese", id: "Indonesian", ms: "Malay", fil: "Filipino",
};

const STATUS_CONFIG = {
  pending_payment: { label: "Awaiting Payment", color: "bg-warning/10 text-warning", icon: "clock" },
  pending:      { label: "Pending",     color: "bg-muted-bg text-muted",           icon: "clock" },
  processing:   { label: "Processing",  color: "bg-accent/10 text-accent",         icon: "spinner" },
  transcribing: { label: "Transcribing", color: "bg-accent/10 text-accent",        icon: "spinner" },
  translating:  { label: "Translating", color: "bg-accent/10 text-accent",         icon: "spinner" },
  voiceover:    { label: "Voiceover",   color: "bg-accent/10 text-accent",         icon: "spinner" },
  rendering:    { label: "Rendering",   color: "bg-accent/10 text-accent",         icon: "spinner" },
  lip_syncing:  { label: "Lip Syncing", color: "bg-accent/10 text-accent",         icon: "spinner" },
  completed:    { label: "Completed",   color: "bg-primary/10 text-primary",       icon: "check" },
  failed:       { label: "Failed",      color: "bg-destructive/10 text-destructive", icon: "x" },
};

const CREDIT_PRICE = 0.30;

/**
 * Dashboard page — displays dubbing jobs with status and downloads.
 */
export default function DashboardPage() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [jobOutputs, setJobOutputs] = useState({}); // { jobId: [outputs] }
  const [downloadingId, setDownloadingId] = useState(null);
  const pollRef = useRef(null);

  const baseUrl = process.env.BASE_URL || "/api/v1";
  const authHeaders = useCallback(() => ({
    "Authorization": `Bearer ${store.get("token") || ""}`,
    "Content-Type": "application/json",
  }), []);

  // Fetch user profile
  useEffect(() => {
    dispatch(
      meRequest({
        callback: (_resp, err) => {
          setLoading(false);
          if (err) setError(err);
        },
      }),
    );
  }, [dispatch]);

  // Fetch dubbing jobs
  const fetchJobs = useCallback(async () => {
    try {
      const resp = await fetch(`${baseUrl}/dubbing_jobs`, { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setJobs(data.dubbing_jobs || []);
      }
    } catch (e) {
      console.error("Failed to fetch jobs:", e);
    } finally {
      setJobsLoading(false);
    }
  }, [baseUrl, authHeaders]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Auto-verify payment when returning from Stripe with ?payment=success
  const paymentVerifiedRef = useRef(false);
  useEffect(() => {
    if (paymentVerifiedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') !== 'success') return;
    if (jobs.length === 0) return;

    const pendingPaymentJobs = jobs.filter((j) => j.status === "pending_payment");
    if (pendingPaymentJobs.length === 0) return;

    paymentVerifiedRef.current = true;

    const verifyAll = async () => {
      for (const job of pendingPaymentJobs) {
        try {
          await fetch(`${baseUrl}/dubbing_jobs/${job.id}/verify_payment`, {
            method: "POST",
            headers: authHeaders(),
          });
        } catch (e) {
          console.error(`Failed to verify payment for job #${job.id}:`, e);
        }
      }
      // Re-fetch jobs after verifying
      fetchJobs();
    };

    verifyAll();
  }, [jobs, baseUrl, authHeaders, fetchJobs]);

  // Poll for processing jobs every 10s
  useEffect(() => {
    const hasProcessing = jobs.some((j) =>
      !["completed", "failed"].includes(j.status)
    );
    if (hasProcessing) {
      pollRef.current = setInterval(fetchJobs, 10000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobs, fetchJobs]);

  // Fetch outputs for a job
  const fetchOutputs = async (jobId) => {
    try {
      const resp = await fetch(`${baseUrl}/dubbing_jobs/${jobId}/outputs`, { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setJobOutputs((prev) => ({ ...prev, [jobId]: data.outputs || [] }));
      }
    } catch (e) {
      console.error("Failed to fetch outputs:", e);
    }
  };

  const toggleExpand = (jobId) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
    } else {
      setExpandedJobId(jobId);
      if (!jobOutputs[jobId]) fetchOutputs(jobId);
    }
  };

  // Download original uploaded file (job-level)
  const handleDownloadOriginal = async (jobId) => {
    const dlKey = `${jobId}_original_file`;
    setDownloadingId(dlKey);
    try {
      const resp = await fetch(`${baseUrl}/dubbing_jobs/${jobId}/download_original`, { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        if (data.download_url) {
          const a = document.createElement("a");
          a.href = data.download_url;
          a.download = data.filename || "download";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      }
    } catch (e) {
      console.error("Download original failed:", e);
    } finally {
      setDownloadingId(null);
    }
  };

  // Download an output (type: "default", "dubbed_audio", "subtitle", "original_audio")
  const handleDownload = async (jobId, outputId, type = "default") => {
    const dlKey = `${outputId}_${type}`;
    setDownloadingId(dlKey);
    try {
      const url = type && type !== "default"
        ? `${baseUrl}/dubbing_jobs/${jobId}/outputs/${outputId}/download?type=${type}`
        : `${baseUrl}/dubbing_jobs/${jobId}/outputs/${outputId}/download`;
      const resp = await fetch(url, { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        if (data.download_url) {
          const a = document.createElement("a");
          a.href = data.download_url;
          a.download = data.filename || "download";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      }
    } catch (e) {
      console.error("Download failed:", e);
    } finally {
      setDownloadingId(null);
    }
  };

  // Helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  // Stats
  const totalJobs = jobs.length;
  const processingJobs = jobs.filter((j) => !["completed", "failed"].includes(j.status)).length;
  const totalCredits = jobs.reduce((sum, j) => sum + (parseFloat(j.total_credits) || 0), 0);

  if (loading) {
    return (
      <div className="flex flex-col items-center py-12 text-muted gap-4">
        <div className="w-8 h-8 border-3 border-muted-bg border-t-primary rounded-full animate-spin" />
        <p>Loading your profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-10 text-muted bg-card border border-muted-bg rounded-[16px] shadow-card">
        <p>Unable to load user information.</p>
      </div>
    );
  }

  // Payment success banner
  const urlParams = new URLSearchParams(window.location.search);
  const paymentStatus = urlParams.get('payment');

  return (
    <div>
      {paymentStatus === 'success' && (
        <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-[12px] flex items-center gap-3">
          <svg className="w-5 h-5 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-primary font-medium">Payment successful! Your dubbing job is now processing.</p>
        </div>
      )}
      {paymentStatus === 'cancelled' && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-[12px] flex items-center gap-3">
          <svg className="w-5 h-5 text-destructive flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <p className="text-sm text-destructive font-medium">Payment was cancelled. Your dubbing job has not started.</p>
        </div>
      )}
      <h1 className="text-3xl font-bold text-foreground mb-6">Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-card border border-muted-bg rounded-[12px] p-6 shadow-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-muted">Total Jobs</p>
              <p className="text-2xl font-bold text-foreground">{totalJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-muted-bg rounded-[12px] p-6 shadow-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
              <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-muted">Currently Processing</p>
              <p className="text-2xl font-bold text-foreground">{processingJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-muted-bg rounded-[12px] p-6 shadow-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary-light/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-muted">Credits Used</p>
              <p className="text-2xl font-bold text-foreground">{totalCredits.toFixed(1)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dubbing Jobs Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-foreground">Your Dubbed Videos</h2>
          <button onClick={fetchJobs} className="text-sm text-primary hover:text-primary-hover font-medium flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {jobsLoading ? (
          <div className="bg-card border border-muted-bg rounded-[16px] shadow-card p-12 flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-3 border-muted-bg border-t-primary rounded-full animate-spin" />
            <p className="text-sm text-muted">Loading your jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="bg-card border border-muted-bg rounded-[16px] shadow-card p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted-bg/50 flex items-center justify-center">
              <svg className="w-8 h-8 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No videos dubbed yet</h3>
            <p className="text-sm text-muted mb-6">Get started by uploading and dubbing your first video</p>
            <a href="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white font-medium rounded-[12px] hover:bg-primary-hover transition-colors">
              Dub Your First Video
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => {
              const sc = getStatusConfig(job.status);
              const isExpanded = expandedJobId === job.id;
              const isProcessing = !["completed", "failed"].includes(job.status);
              const outputs = jobOutputs[job.id] || [];

              return (
                <div key={job.id} className="bg-card border border-muted-bg rounded-[16px] shadow-card overflow-hidden">
                  {/* Job Header */}
                  <button
                    type="button"
                    onClick={() => toggleExpand(job.id)}
                    className="w-full text-left px-6 py-4 hover:bg-secondary/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0 ${
                          job.input_type === "video" ? "bg-primary/10" : "bg-accent/10"
                        }`}>
                          {job.input_type === "video" ? (
                            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                          )}
                        </div>

                        {/* Title & meta */}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-foreground truncate">{job.title || `Job #${job.id}`}</p>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="text-xs text-muted">{formatDuration(job.duration_seconds)}</span>
                            <span className="text-xs text-muted">
                              {(job.target_languages || []).map((l) => LANGUAGE_NAMES[l] || l).join(", ")}
                            </span>
                            <span className="text-xs text-muted">{formatDate(job.created_at)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        {/* Status badge */}
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${sc.color}`}>
                          {sc.icon === "spinner" && (
                            <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                          )}
                          {sc.icon === "check" && (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {sc.icon === "x" && (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                          {sc.icon === "clock" && (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          {sc.label}
                        </span>

                        {/* Chevron */}
                        <svg className={`w-5 h-5 text-muted transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Processing progress bar */}
                    {isProcessing && (
                      <div className="mt-3">
                        <div className="w-full h-1.5 bg-muted-bg rounded-full overflow-hidden">
                          <div className="h-full bg-accent rounded-full animate-pulse" style={{ width: "60%" }} />
                        </div>
                      </div>
                    )}
                  </button>

                  {/* Expanded: Job details & outputs */}
                  {isExpanded && (
                    <div className="border-t border-muted-bg px-6 py-4">
                      {/* Job info row */}
                      <div className="flex flex-wrap gap-4 mb-4 text-xs text-muted">
                        <span>Credits: <strong className="text-foreground">{job.total_credits}</strong></span>
                        <span>Voice: <strong className="text-foreground capitalize">{job.voice_mode}</strong></span>
                        {job.lip_sync && <span className="text-primary font-medium">Lip Sync</span>}
                        {job.subtitles && <span className="text-primary font-medium">Subtitles</span>}
                        {job.completed_at && <span>Completed: <strong className="text-foreground">{formatDate(job.completed_at)}</strong></span>}
                      </div>

                      {/* Failed state */}
                      {job.status === "failed" && (
                        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-[8px] mb-4">
                          <p className="text-sm text-destructive">Job failed. Please try again or contact support.</p>
                        </div>
                      )}

                      {/* Pending Payment state */}
                      {job.status === "pending_payment" && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-[12px] mb-4">
                          <div className="flex items-center gap-3 mb-3">
                            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-amber-800">Payment required to start this job</p>
                              <p className="text-xs text-amber-600 mt-0.5">
                                {job.total_credits} credits needed &middot; ${(parseFloat(job.total_credits) * CREDIT_PRICE + 0.50).toFixed(2)} total
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={async () => {
                                try {
                                  const resp = await fetch(`${baseUrl}/dubbing_jobs/${job.id}/pay`, {
                                    method: "POST",
                                    headers: authHeaders(),
                                  });
                                  if (resp.ok) {
                                    const data = await resp.json();
                                    if (data.checkout_url) {
                                      window.location.href = data.checkout_url;
                                    }
                                  }
                                } catch (e) {
                                  console.error("Pay failed:", e);
                                }
                              }}
                              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-medium rounded-[10px] hover:bg-primary-hover transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              Pay Now
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const resp = await fetch(`${baseUrl}/dubbing_jobs/${job.id}/verify_payment`, {
                                    method: "POST",
                                    headers: authHeaders(),
                                  });
                                  if (resp.ok) {
                                    const data = await resp.json();
                                    if (data.dubbing_job?.status !== "pending_payment") {
                                      fetchJobs();
                                    } else {
                                      alert(data.message || "Payment not yet completed");
                                    }
                                  }
                                } catch (e) {
                                  console.error("Verify failed:", e);
                                }
                              }}
                              className="flex items-center gap-1.5 px-4 py-2 bg-card text-foreground text-sm font-medium rounded-[10px] border border-muted-bg hover:bg-secondary transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Already Paid? Check Status
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Processing state */}
                      {isProcessing && job.status !== "pending_payment" && (
                        <div className="p-4 bg-accent/5 border border-accent/20 rounded-[12px] flex items-center gap-3">
                          <div className="w-6 h-6 border-2 border-accent/30 border-t-accent rounded-full animate-spin flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-foreground">Currently {sc.label.toLowerCase()}...</p>
                            <p className="text-xs text-muted mt-0.5">This may take a few minutes. The page will auto-refresh.</p>
                          </div>
                        </div>
                      )}

                      {/* Completed: show outputs */}
                      {job.status === "completed" && (
                        <div>
                          {/* Original file download */}
                          <div className="mb-4 p-3 bg-secondary/50 border border-muted-bg rounded-[10px] flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0 ${
                                job.input_type === "video" ? "bg-muted-bg" : "bg-muted-bg"
                              }`}>
                                {job.input_type === "video" ? (
                                  <svg className="w-4 h-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">Original {job.input_type === "video" ? "Video" : "Audio"}</p>
                                <p className="text-xs text-muted">Source file</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDownloadOriginal(job.id)}
                              disabled={downloadingId === `${job.id}_original_file`}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[8px] transition-colors disabled:opacity-50 bg-card text-foreground border border-muted-bg hover:bg-secondary/80"
                            >
                              {downloadingId === `${job.id}_original_file` ? (
                                <div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              )}
                              Download
                            </button>
                          </div>

                          <h4 className="text-sm font-semibold text-foreground mb-3">Output Files</h4>
                          {outputs.length === 0 ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                              <span className="ml-2 text-sm text-muted">Loading outputs...</span>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {outputs.map((output) => {
                                const langName = output.language_name || LANGUAGE_NAMES[output.language] || output.language;

                                if (output.status === "failed") {
                                  return (
                                    <div key={output.id} className="p-3 bg-destructive/5 border border-destructive/20 rounded-[10px] flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-[6px] bg-destructive/10 flex items-center justify-center flex-shrink-0">
                                          <svg className="w-4 h-4 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                        </div>
                                        <p className="text-sm font-medium text-foreground">{langName}</p>
                                      </div>
                                      <span className="text-xs text-destructive font-medium">Failed</span>
                                    </div>
                                  );
                                }

                                if (output.status !== "completed") {
                                  return (
                                    <div key={output.id} className="p-3 bg-secondary/50 border border-muted-bg rounded-[10px] flex items-center gap-3">
                                      <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
                                      <p className="text-sm text-muted">{langName} — Processing...</p>
                                    </div>
                                  );
                                }

                                const DownloadBtn = ({ label, icon, type, variant = "default" }) => {
                                  const dlKey = `${output.id}_${type}`;
                                  const isLoading = downloadingId === dlKey;
                                  const styles = variant === "primary"
                                    ? "bg-primary text-white hover:bg-primary-hover"
                                    : "bg-card text-foreground border border-muted-bg hover:bg-secondary/80";
                                  return (
                                    <button
                                      onClick={() => handleDownload(job.id, output.id, type)}
                                      disabled={isLoading}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[8px] transition-colors disabled:opacity-50 ${styles}`}
                                    >
                                      {isLoading ? (
                                        <div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                                      ) : (
                                        icon
                                      )}
                                      {label}
                                    </button>
                                  );
                                };

                                return (
                                  <div key={output.id} className="p-4 bg-secondary/50 border border-muted-bg rounded-[12px]">
                                    <div className="flex items-center gap-3 mb-3">
                                      <div className="w-8 h-8 rounded-[6px] bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                        </svg>
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-foreground">{langName}</p>
                                        <p className="text-xs text-muted">Ready to download</p>
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                      {/* Dubbed Video (for video jobs) or Dubbed Audio (for audio jobs) */}
                                      {output.has_video && (
                                        <DownloadBtn
                                          label="Dubbed Video"
                                          type="default"
                                          variant="primary"
                                          icon={
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                          }
                                        />
                                      )}

                                      {output.has_audio && (
                                        <DownloadBtn
                                          label={output.has_video ? "Dubbed Audio" : "Dubbed Audio"}
                                          type={output.has_video ? "dubbed_audio" : "default"}
                                          variant={output.has_video ? "default" : "primary"}
                                          icon={
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                            </svg>
                                          }
                                        />
                                      )}

                                      {output.has_subtitle && (
                                        <DownloadBtn
                                          label="Subtitles (.srt)"
                                          type="subtitle"
                                          icon={
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                          }
                                        />
                                      )}

                                      {output.has_original_audio && (
                                        <DownloadBtn
                                          label="Original Audio"
                                          type="original_audio"
                                          icon={
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                            </svg>
                                          }
                                        />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
